// =============================================================================
// src/lib/commandBarCore.js - pure helpers for the global command bar
// (components/CommandPalette.jsx, design round 3, Oct 2026).
//
//   parseVerb(q)          "book jor" -> { verb: "book", rest: "jor" }
//   patientActions(perms, { canCheckInToday })
//                         the per-patient actions this role may run, in order
//   quickActions(perms)   the empty-box "Quick actions" (pick the patient next)
//   readRecentPatients / pushRecentPatient
//                         per-user recent charts in localStorage: patient
//                         uuids and opened-at times ONLY. Names are resolved
//                         at render by the RLS-scoped patients read, so
//                         nothing identifying sits in browser storage.
//                         The key is still removed at every sign-out
//                         (lib/localPhi.js): on a shared workstation the
//                         ids alone tell the next user whose charts were open.
//   patientIdFromPath     /patients/<uuid>[/...] -> uuid
//   openedAgo(at, now)    "Opened 10 minutes ago"
//
// No imports, so node:test can load it (src/lib/__tests__/commandBar.test.mjs).
// =============================================================================

// Verbs a query can start with. The first word(s) must match exactly, so a
// patient called "Booker" or the "Tasks" page never turns into a verb.
const VERB_PATTERNS = [
  ["checkin",    /^check[\s-]?in(\s+|$)/i],
  ["touchpoint", /^(log\s+)?touchpoint(\s+|$)/i],
  ["book",       /^(book|schedule)(\s+|$)/i],
  ["message",    /^message(\s+|$)/i],
  ["task",       /^(new\s+)?task(\s+|$)/i],
  ["referral",   /^(refer|referral|new\s+referral)(\s+|$)/i],
];

export function parseVerb(q) {
  const s = String(q || "").replace(/^\s+/, "");
  for (const [verb, re] of VERB_PATTERNS) {
    const m = s.match(re);
    if (m) return { verb, rest: s.slice(m[0].length).trim() };
  }
  return null;
}

// Every patient action the bar knows, in panel order. `perm` names the key in
// the perms object the component builds from the role's own gates (the same
// nav and role helpers the destination screens use).
export const PATIENT_ACTIONS = [
  { key: "book",       label: "Book a visit",   chip: "Book a visit",   hotkey: "B", perm: "book" },
  { key: "message",    label: "Send a message", chip: "Send a message", hotkey: "M", perm: "message" },
  { key: "task",       label: "New task",       chip: "New task",       hotkey: "T", perm: "task" },
  { key: "checkin",    label: "Check in",       chip: "Check in",       hotkey: "I", perm: "checkIn" },
  { key: "referral",   label: "New referral",   chip: "New referral",   hotkey: "R", perm: "referral" },
  { key: "touchpoint", label: "Log touchpoint", chip: "Log touchpoint", hotkey: "L", perm: "touchpoint" },
  { key: "chart",      label: "Open chart",     chip: null,             hotkey: "Enter", perm: null },
];

export const actionByKey = (key) => PATIENT_ACTIONS.find((a) => a.key === key) || null;

// Check in is offered only for a patient with a visit today that can still be
// checked in (scheduleCore.quickActions decides that; the caller passes the
// answer). Open chart is always there.
export function patientActions(perms, { canCheckInToday = false } = {}) {
  const p = perms || {};
  return PATIENT_ACTIONS.filter((a) => {
    if (!a.perm) return true;
    if (!p[a.perm]) return false;
    if (a.key === "checkin") return !!canCheckInToday;
    return true;
  });
}

export function quickActions(perms) {
  const p = perms || {};
  const labels = { checkin: "Check in a patient" };
  return PATIENT_ACTIONS
    .filter((a) => a.perm && p[a.perm] && a.key !== "message")
    .map((a) => ({ ...a, label: labels[a.key] || a.label }));
}

// A verb the role cannot run is not a verb: "book jor" for a Care Manager is
// just a search, never a chip that leads nowhere.
export function verbFor(q, perms) {
  const v = parseVerb(q);
  if (!v) return null;
  const a = actionByKey(v.verb);
  return a && a.perm && (perms || {})[a.perm] ? v : null;
}

// ---- Recent patients ----------------------------------------------------------
export const RECENT_PATIENTS_KEY = "practiceos.commandBar.recentPatients";
export const MAX_RECENT_PATIENTS = 5;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function patientIdFromPath(pathname) {
  const m = String(pathname || "").match(/^\/patients\/([0-9a-f-]{36})(\/|$)/i);
  return m && UUID.test(m[1]) ? m[1].toLowerCase() : null;
}

function readAll(storage) {
  try {
    const raw = JSON.parse((storage && storage.getItem(RECENT_PATIENTS_KEY)) || "{}");
    return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  } catch (e) {
    return {};
  }
}

// -> [{ id, at }] newest first. Anything that is not a uuid + number is
// dropped, so a hand-edited or older shape can never put text on screen.
export function readRecentPatients(storage, userId) {
  if (!userId) return [];
  const list = readAll(storage)[userId];
  if (!Array.isArray(list)) return [];
  return list
    .filter((r) => r && typeof r.id === "string" && UUID.test(r.id) && Number.isFinite(r.at))
    .slice(0, MAX_RECENT_PATIENTS)
    .map((r) => ({ id: r.id, at: r.at }));
}

export function pushRecentPatient(storage, userId, patientId, at = Date.now()) {
  if (!userId || !patientId || !UUID.test(patientId)) return;
  try {
    const all = readAll(storage);
    const prev = readRecentPatients(storage, userId).filter((r) => r.id !== patientId);
    all[userId] = [{ id: patientId, at }, ...prev].slice(0, MAX_RECENT_PATIENTS);
    storage.setItem(RECENT_PATIENTS_KEY, JSON.stringify(all));
  } catch (e) {
    // Storage blocked or full (private mode, quota). Recents are a
    // convenience, never a correctness requirement.
  }
}

export function openedAgo(at, now = Date.now()) {
  if (!Number.isFinite(at)) return null;
  const min = Math.max(0, Math.round((now - at) / 60000));
  if (min < 1) return "Opened just now";
  if (min < 60) return "Opened " + min + (min === 1 ? " minute" : " minutes") + " ago";
  const h = Math.round(min / 60);
  if (h < 24) return "Opened " + h + (h === 1 ? " hour" : " hours") + " ago";
  const d = Math.round(h / 24);
  return d === 1 ? "Opened yesterday" : "Opened " + d + " days ago";
}

// "1968-03-14" -> "03/14/1968"; anything else passes through.
export function fmtDob(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? m[2] + "/" + m[3] + "/" + m[1] : (iso || "");
}
